import { computed, defineComponent, watch } from 'vue'

// Utils
import { extend, pick } from '@xzx-design/utils'
import {
  useCustomFieldValue,
  useExpose,
  useNamespace,
  useParent,
} from '@xzx-design/hooks'
import { CHECKBOX_GROUP_KEY } from './types'
import { checkboxProps } from './checkbox'

// Components
import Checker from './checker.tsx'

// Types
import type { CheckboxExpose } from './types'

const name = 'XzxCheckbox'

export default defineComponent({
  name,
  __name: name,
  props: checkboxProps,
  emits: ['change', 'update:modelValue'],
  setup(props, { emit, slots }) {
    const { parent } = useParent(CHECKBOX_GROUP_KEY)
    const ns = useNamespace('checkbox')

    const setParentValue = (checked: boolean) => {
      const { name } = props
      const { max, modelValue } = parent!.props
      const value = modelValue.slice()

      if (checked) {
        const overlimit = max && value.length >= +max

        if (!overlimit && !value.includes(name)) {
          value.push(name)

          if (props.bindGroup) {
            parent!.updateValue(value)
          }
        }
      } else {
        const index = value.indexOf(name)

        if (index !== -1) {
          value.splice(index, 1)

          if (props.bindGroup) {
            parent!.updateValue(value)
          }
        }
      }
    }

    const checked = computed(() => {
      if (parent && props.bindGroup) {
        return parent.props.modelValue?.indexOf(props.name) !== -1
      }
      return !!props.modelValue
    })

    const toggle = (newValue = !checked.value) => {
      if (parent && props.bindGroup) {
        setParentValue(newValue)
      } else {
        emit('update:modelValue', newValue)
      }

      if (props.indeterminate !== null) emit('change', newValue)
    }

    watch(
      () => props.modelValue,
      (value) => {
        if (props.indeterminate === null) emit('change', value)
      }
    )

    useExpose<CheckboxExpose>({ toggle, props, checked })
    useCustomFieldValue(() => props.modelValue)

    return () => (
      <Checker
        v-slots={pick(slots, ['default', 'icon'])}
        namespace={ns}
        role="checkbox"
        parent={parent}
        checked={checked.value}
        onToggle={toggle}
        {...props}
      />
    )
  },
})
